import type { ReactNode } from 'react'
import { Box, HStack, Heading, Text, VStack } from '@chakra-ui/react'
import { Card } from '../../shared'
import { FollowUpItemList } from './FollowUpItemList'
import type { FollowUpItemRowDto, FollowUpKind, FollowUpKindCountDto } from '../../../types/followUp'

export interface FollowUpSectionCardProps {
  campaignId: string
  kind: FollowUpKind
  customKindId?: string
  title: string
  counts: FollowUpKindCountDto
  /** CF-1 contract: the section's action slot — empty in P1; P2/P4/P5 fill it via FollowUpTab.sectionSlots. */
  actions?: ReactNode
  /** Passed to the default list (FollowUpTab.rowActions pass-through). */
  rowActions?: (row: FollowUpItemRowDto) => ReactNode
  /** Defaults to <FollowUpItemList campaignId kind customKindId />. */
  children?: ReactNode
}

export function FollowUpSectionCard({
  campaignId, kind, customKindId, title, counts, actions, rowActions, children,
}: FollowUpSectionCardProps) {
  const statusCounts: { label: string; value: number; always?: boolean }[] = [
    { label: 'Open', value: counts.open, always: true },
    { label: 'Done', value: counts.done, always: true },
    { label: "Couldn't", value: counts.couldnt },
    { label: 'Cancelled', value: counts.cancelled },
    { label: 'Superseded', value: counts.superseded },
    { label: 'Declined', value: counts.declined },
  ]

  return (
    <Card data-testid="follow-up-section" p={{ base: 4, md: 6 }}>
      <Box minW={0} mb={4}>
        <Heading size="md" data-testid="follow-up-section-title">{title}</Heading>
        <HStack gap={3} mt={1} flexWrap="wrap">
          {statusCounts.filter(c => c.always || c.value > 0).map(c => (
            <Text key={c.label} fontSize="sm" color="muted">
              {c.label}{' '}
              <Text as="span" fontWeight="semibold" css={{ fontVariantNumeric: 'tabular-nums' }}>{c.value}</Text>
            </Text>
          ))}
        </HStack>
      </Box>
      {/* Slot content (P2 route panel, P4/P5) is a full-width block below the header, never beside the
          title: it can hold tables and a map. */}
      {!!actions && (
        <VStack data-testid="follow-up-section-actions" w="full" minW={0} align="stretch" gap={4} mb={4}>
          {actions}
        </VStack>
      )}
      {children ?? (
        <FollowUpItemList campaignId={campaignId} kind={kind} customKindId={customKindId} rowActions={rowActions} />
      )}
    </Card>
  )
}
